import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { useAdminApi } from './hooks/useAdminApi';
import { AdminBreadcrumbs } from './components/AdminUiPrimitives';
import { AdminPageHeader } from './components/AdminMasterDetail';
import ScheduleSessionsCalendar from './components/ScheduleSessionsCalendar';
import ScheduleSessionsPanel, { ScheduleSessionItem, SessionStatus, UpdateScheduleSessionRequest } from './components/ScheduleSessionsPanel';

type SessionScope = 'all' | 'my';

type ManageSessionsProps = {
  scope?: SessionScope;
};

type RecurrenceType = 'None' | 'Weekly';
type LocationType = 'InPerson' | 'Online';
type ScheduleStatus = 'Scheduled' | 'Completed' | 'Cancelled';

interface ClassSchedule {
  id: number;
  organizationId: number;
  classroomId: number;
  classroomName: string;
  teacherUserId: number;
  teacherDisplayName: string;
  title: string;
  startDateTime: string;
  endDateTime: string;
  recurrenceType: RecurrenceType;
  recurrenceEndDate?: string | null;
  locationType: LocationType;
  location?: string | null;
  meetingUrl?: string | null;
  status: ScheduleStatus;
  notes?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const API_BASE_URL = `${config.apiBaseUrl}/api`;

const mapSessionRow = (item: any): ClassSchedule => ({
  id: item.id,
  organizationId: item.organizationId ?? 0,
  classroomId: item.classroomId ?? 0,
  classroomName: item.classroomName ?? '—',
  teacherUserId: item.teacherUserId ?? 0,
  teacherDisplayName: item.teacherDisplayName ?? '',
  title: item.title ?? 'Session',
  startDateTime: item.startDateTime ?? item.startDate ?? new Date().toISOString(),
  endDateTime: item.endDateTime ?? item.endDate ?? new Date().toISOString(),
  recurrenceType: item.recurrenceType ?? 'None',
  recurrenceEndDate: item.recurrenceEndDate ?? null,
  locationType: item.locationType ?? (item.meetingUrl ? 'Online' : 'InPerson'),
  location: item.location ?? null,
  meetingUrl: item.meetingUrl ?? null,
  status: item.status ?? 'Scheduled',
  notes: item.notes ?? null,
  isActive: true,
  createdAt: item.createdAt ?? new Date().toISOString(),
  updatedAt: item.updatedAt ?? new Date().toISOString(),
});

const toDateInput = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const ManageSessions: React.FC<ManageSessionsProps> = ({ scope = 'all' }) => {
  const navigate = useNavigate();
  const { hasActiveOrganization, getManagementToken, getOrganizationHeaders } = useAdminApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ClassSchedule[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{ startDateTime: string; endDateTime: string; status: SessionStatus; location: string; meetingUrl: string; notes: string } | null>(null);
  const [savingSessionId, setSavingSessionId] = useState<number | null>(null);

  const isMyScope = scope === 'my';
  const pageTitle = isMyScope ? 'My Sessions' : 'All Sessions';

  const loadSessions = useCallback(async () => {
    if (!hasActiveOrganization) {
      setError('No organization selected. Please switch organization first.');
      setSessions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);

      let result: any[] = [];

      if (isMyScope) {
        const response = await axios.get<any[]>(`${API_BASE_URL}/schedules/my?upcomingOnly=false`, { headers });
        result = Array.isArray(response.data) ? response.data : [];
      } else {
        const response = await axios.get<any[]>(`${API_BASE_URL}/schedules`, { headers });
        const schedules = Array.isArray(response.data) ? response.data : [];
        result = schedules.flatMap((schedule) => {
          const sessionRows = Array.isArray(schedule.sessions) ? schedule.sessions : [];
          return sessionRows.map((session: any) => mapSessionRow({
            ...session,
            title: schedule.title ?? 'Session',
            location: session.location ?? schedule.location ?? null,
            meetingUrl: session.meetingUrl ?? schedule.meetingUrl ?? null,
            recurrenceType: schedule.recurrenceType ?? 'None',
            recurrenceEndDate: schedule.recurrenceEndDate ?? null,
          }));
        });
      }

      setSessions(result.map((row) => mapSessionRow(row)));
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load session data.');
    } finally {
      setLoading(false);
    }
  }, [getManagementToken, getOrganizationHeaders, hasActiveOrganization, isMyScope]);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    setViewMode('list');
  }, [scope]);

  useEffect(() => {
    if (sessions.length === 0) {
      setSelectedSessionId(null);
      return;
    }

    if (selectedSessionId === null || !sessions.some((session) => session.id === selectedSessionId)) {
      setSelectedSessionId(sessions[0].id);
    }
  }, [selectedSessionId, sessions]);

  const filteredSessions = useMemo(() => sessions, [sessions]);

  const sessionItems: ScheduleSessionItem[] = useMemo(
    () => filteredSessions.map((session) => ({
      id: session.id,
      scheduleId: session.id,
      organizationId: session.organizationId,
      classroomId: session.classroomId,
      classroomName: session.classroomName,
      teacherUserId: session.teacherUserId,
      teacherDisplayName: session.teacherDisplayName,
      studentUserId: null,
      studentDisplayName: '',
      targetType: 'Classroom',
      startDateTime: session.startDateTime,
      endDateTime: session.endDateTime,
      status: session.status as SessionStatus,
      isModified: false,
      location: session.location,
      meetingUrl: session.meetingUrl,
      notes: session.notes,
    })),
    [filteredSessions]
  );

  const selectedSession = useMemo(
    () => sessions.find((session) => session.id === selectedSessionId) || null,
    [selectedSessionId, sessions]
  );

  const beginEdit = (session: {
    id: number;
    startDateTime: string;
    endDateTime: string;
    status: SessionStatus | string;
    location?: string | null;
    meetingUrl?: string | null;
    notes?: string | null;
  }) => {
    setEditingSessionId(session.id);
    setEditForm({
      startDateTime: new Date(session.startDateTime).toISOString().slice(0, 16),
      endDateTime: new Date(session.endDateTime).toISOString().slice(0, 16),
      status: (session.status as SessionStatus) || 'Scheduled',
      location: session.location || '',
      meetingUrl: session.meetingUrl || '',
      notes: session.notes || '',
    });
    setError(null);
  };

  const cancelEdit = () => {
    setEditingSessionId(null);
    setEditForm(null);
    setError(null);
  };

  const saveEdit = async (sessionId: number) => {
    if (!editForm) {
      return;
    }

    const start = new Date(editForm.startDateTime);
    const end = new Date(editForm.endDateTime);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      setError('Please provide valid start and end date-time values.');
      return;
    }

    if (end <= start) {
      setError('End date-time must be after start date-time.');
      return;
    }

    try {
      setSavingSessionId(sessionId);
      setError(null);
      await onUpdateSession(sessionId, {
        startDateTime: start.toISOString(),
        endDateTime: end.toISOString(),
        status: editForm.status,
        location: editForm.location.trim() || null,
        meetingUrl: editForm.meetingUrl.trim() || null,
        notes: editForm.notes.trim() || null,
      });
      cancelEdit();
    } finally {
      setSavingSessionId(null);
    }
  };

  const onUpdateSession = async (sessionId: number, payload: UpdateScheduleSessionRequest) => {
    if (!hasActiveOrganization) {
      throw new Error('No organization selected. Please switch organization first.');
    }

    const token = await getManagementToken();
    const headers = getOrganizationHeaders(token);
    await axios.put(`${API_BASE_URL}/schedules/sessions/${sessionId}`, payload, { headers });
    await loadSessions();
  };

  const onStartSession = async (sessionId: number) => {
    if (!hasActiveOrganization) {
      throw new Error('No organization selected. Please switch organization first.');
    }

    const token = await getManagementToken();
    const headers = getOrganizationHeaders(token);
    await axios.post(`${API_BASE_URL}/schedules/sessions/${sessionId}/start`, {}, { headers });
    await loadSessions();
  };

  const onCompleteSession = async (sessionId: number) => {
    if (!hasActiveOrganization) {
      throw new Error('No organization selected. Please switch organization first.');
    }

    const token = await getManagementToken();
    const headers = getOrganizationHeaders(token);
    await axios.post(`${API_BASE_URL}/schedules/sessions/${sessionId}/complete`, {}, { headers });
    await loadSessions();
  };

  const onRecordAttendance = async (sessionId: number, payload: { userId: number; status: string; joinedAt?: string | null; leftAt?: string | null; notes?: string | null; }) => {
    if (!hasActiveOrganization) {
      throw new Error('No organization selected. Please switch organization first.');
    }

    const token = await getManagementToken();
    const headers = getOrganizationHeaders(token);
    await axios.post(`${API_BASE_URL}/schedules/sessions/${sessionId}/attendance`, payload, { headers });
    await loadSessions();
  };

  return (
    <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12, gridTemplateRows: 'auto auto auto 1fr' }}>
      <AdminBreadcrumbs
        items={[
          { label: 'Administration', to: '/analytics' },
          { label: pageTitle, isCurrent: true },
        ]}
      />

      <AdminPageHeader
        title={pageTitle}
        subtitle={`${sessions.length} ${isMyScope ? 'sessions for you' : 'total sessions'}`}
        actions={[
          {
            key: 'all-sessions',
            label: 'All sessions',
            variant: 'secondary',
            onClick: () => navigate('/manage-schedule/all'),
          },
          {
            key: 'my-sessions',
            label: 'My sessions',
            variant: 'secondary',
            onClick: () => navigate('/manage-schedule/mysessions'),
          },
          {
            key: 'schedule-class',
            label: 'Schedule Class',
            variant: 'primary',
            onClick: () => navigate('/manage-schedule/new'),
          },
          {
            key: 'refresh',
            label: 'Refresh',
            variant: 'secondary',
            onClick: () => { void loadSessions(); },
          },
        ]}
      />

      {!hasActiveOrganization && (
        <div style={{ borderRadius: 10, border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e', padding: '10px 12px', fontSize: '0.84rem' }}>
          No organization selected. Please switch organization first.
        </div>
      )}

      {error && (
        <div style={{ borderRadius: 10, border: '#fecaca', background: '#fff1f2', color: '#b91c1c', padding: '10px 12px', fontSize: '0.84rem' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="amd-btn"
          onClick={() => setViewMode('list')}
          style={{
            background: viewMode === 'list' ? '#1d4ed8' : '#fff',
            color: viewMode === 'list' ? '#fff' : '#1e293b',
            border: `1px solid ${viewMode === 'list' ? '#1d4ed8' : '#dfe8f8'}`,
          }}
        >
          List
        </button>
        <button
          type="button"
          className="amd-btn"
          onClick={() => setViewMode('calendar')}
          style={{
            background: viewMode === 'calendar' ? '#1d4ed8' : '#fff',
            color: viewMode === 'calendar' ? '#fff' : '#1e293b',
            border: `1px solid ${viewMode === 'calendar' ? '#1d4ed8' : '#dfe8f8'}`,
          }}
        >
          Calendar
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(520px, 58%) minmax(340px, 42%)', gap: 12, minHeight: 460 }}>
        <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', overflow: 'hidden', minHeight: 420, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #edf2fb', fontWeight: 700, color: '#173159' }}>
            {viewMode === 'calendar' ? 'Session Calendar' : 'Session List'}
          </div>

          {loading ? (
            <div style={{ padding: 14, color: '#475569' }}>Loading sessions...</div>
          ) : filteredSessions.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, padding: 14, color: '#64748b' }}>No sessions found.</div>
          ) : viewMode === 'calendar' ? (
            <div style={{ padding: 12, flex: 1, minHeight: 300 }}>
              <ScheduleSessionsCalendar sessions={sessionItems} />
            </div>
          ) : (
            <div style={{ overflow: 'auto', flex: 1, padding: 8 }}>
              <div style={{ display: 'grid', gap: 8 }}>
                {sessionItems.map((session) => {
                  const isEditing = editingSessionId === session.id;
                  const isSaving = savingSessionId === session.id;

                  return (
                    <div key={session.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#fff', display: 'grid', gap: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ fontWeight: 700, color: '#0f172a' }}>{new Date(session.startDateTime).toLocaleString()}</div>
                        <span style={{ borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 700, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#334155' }}>
                          {session.status}
                        </span>
                      </div>

                      {!isEditing ? (
                        <>
                          <div style={{ fontSize: 13, color: '#475569' }}>
                            <div><strong>Teacher:</strong> {session.teacherDisplayName || `User ${session.teacherUserId}`}</div>
                            <div><strong>Classroom:</strong> {session.classroomName || '—'}</div>
                            <div><strong>Location:</strong> {session.location || '—'}</div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              className="amd-btn amd-btn-secondary"
                              onClick={() => navigate(`/manage-schedule/session/${session.id}`)}
                            >
                              Open Session
                            </button>
                            <button
                              type="button"
                              className="amd-btn amd-btn-ghost"
                              onClick={() => beginEdit(session)}
                            >
                              Edit
                            </button>
                          </div>
                        </>
                      ) : (
                        <div style={{ display: 'grid', gap: 8, paddingTop: 6, borderTop: '1px solid #e2e8f0' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <label style={{ display: 'grid', gap: 4 }}>
                              <span style={{ fontSize: 12, color: '#475569' }}>Start</span>
                              <input
                                type="datetime-local"
                                value={editForm?.startDateTime || ''}
                                onChange={(event) => setEditForm((previous) => previous ? { ...previous, startDateTime: event.target.value } : previous)}
                              />
                            </label>
                            <label style={{ display: 'grid', gap: 4 }}>
                              <span style={{ fontSize: 12, color: '#475569' }}>End</span>
                              <input
                                type="datetime-local"
                                value={editForm?.endDateTime || ''}
                                onChange={(event) => setEditForm((previous) => previous ? { ...previous, endDateTime: event.target.value } : previous)}
                              />
                            </label>
                          </div>

                          <label style={{ display: 'grid', gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#475569' }}>Status</span>
                            <select
                              value={editForm?.status || 'Scheduled'}
                              onChange={(event) => setEditForm((previous) => previous ? { ...previous, status: event.target.value as SessionStatus } : previous)}
                            >
                              {['Scheduled', 'InProgress', 'Completed', 'Cancelled', 'Rescheduled', 'NoShow'].map((statusOption) => (
                                <option key={statusOption} value={statusOption}>{statusOption}</option>
                              ))}
                            </select>
                          </label>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <label style={{ display: 'grid', gap: 4 }}>
                              <span style={{ fontSize: 12, color: '#475569' }}>Location</span>
                              <input
                                type="text"
                                value={editForm?.location || ''}
                                onChange={(event) => setEditForm((previous) => previous ? { ...previous, location: event.target.value } : previous)}
                              />
                            </label>
                            <label style={{ display: 'grid', gap: 4 }}>
                              <span style={{ fontSize: 12, color: '#475569' }}>Meeting URL</span>
                              <input
                                type="url"
                                value={editForm?.meetingUrl || ''}
                                onChange={(event) => setEditForm((previous) => previous ? { ...previous, meetingUrl: event.target.value } : previous)}
                              />
                            </label>
                          </div>

                          <label style={{ display: 'grid', gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#475569' }}>Notes</span>
                            <textarea
                              rows={2}
                              value={editForm?.notes || ''}
                              onChange={(event) => setEditForm((previous) => previous ? { ...previous, notes: event.target.value } : previous)}
                            />
                          </label>

                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button type="button" className="amd-btn amd-btn-ghost" onClick={cancelEdit} disabled={isSaving}>Cancel</button>
                            <button type="button" className="amd-btn amd-btn-primary" onClick={() => { void saveEdit(session.id); }} disabled={isSaving}>
                              {isSaving ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', padding: 14, minHeight: 420, display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ marginTop: 0, marginBottom: 10, color: '#173159' }}>Session Details</h3>

          {selectedSession ? (
            <div style={{ display: 'grid', gap: 8, fontSize: 14 }}>
              <div><strong>Title:</strong> {selectedSession.title}</div>
              <div><strong>Classroom:</strong> {selectedSession.classroomName}</div>
              <div><strong>Teacher:</strong> {selectedSession.teacherDisplayName}</div>
              <div><strong>Start:</strong> {formatDateTime(selectedSession.startDateTime)}</div>
              <div><strong>End:</strong> {formatDateTime(selectedSession.endDateTime)}</div>
              <div><strong>Recurrence:</strong> {selectedSession.recurrenceType}{selectedSession.recurrenceType === 'Weekly' && selectedSession.recurrenceEndDate ? ` (until ${toDateInput(selectedSession.recurrenceEndDate)})` : ''}</div>
              <div><strong>Location:</strong> {selectedSession.locationType === 'Online' ? selectedSession.meetingUrl || 'Online' : selectedSession.location || 'In Person'}</div>
              <div><strong>Status:</strong> {selectedSession.status}</div>
              <div><strong>Notes:</strong> {selectedSession.notes || '—'}</div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                <button
                  type="button"
                  className="amd-btn amd-btn-secondary"
                  onClick={() => navigate(`/manage-schedule/session/${selectedSession.id}`)}
                >
                  Open Session
                </button>
              </div>
            </div>
          ) : (
            <div style={{ color: '#64748b', fontSize: 14 }}>Select a session to view details.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageSessions;
